import { Node, mergeAttributes } from '@tiptap/core';

/**
 * The end of How to Mentor: pick a person, pick a course, start.
 *
 * Robert, 8 Sep: "shouldn't just ask for a name but a user account that exists
 * or needs to be created/invited to a course to 'try it out' ... three options
 * would be great to choose from or a way for them to choose from another one."
 *
 * Six sessions on mentoring used to end with a reflection box, and 22 approved
 * mentors have between them started zero mentoring relationships. A name typed
 * into a textarea is not a relationship. This asks for a real account, or
 * creates one, and starts the thing while the course is still open.
 *
 * It creates a 'mentorship' group rather than the wizard's 'one_on_one', so the
 * pair reads as mentor and mentee everywhere else: reflectionAudienceService
 * labels the share control "My mentor" off that group type.
 */
export const MentorLaunch = Node.create({
  name: 'mentorLaunch',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      // Slugs of the courses offered first. Anything else is still reachable.
      suggested: {
        default:
          'how-to-study-the-bible,foundations-1-gospel-and-new-identity,foundations-2-connecting-with-god',
        parseHTML: (el) => el.getAttribute('data-suggested') || '',
        renderHTML: (attrs) => ({ 'data-suggested': attrs.suggested }),
      },
      heading: {
        default: 'Start with one person',
        parseHTML: (el) => el.getAttribute('data-heading') || '',
        renderHTML: (attrs) => ({ 'data-heading': attrs.heading }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="mentor-launch"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'mentor-launch' })];
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('div');
      dom.className = 'mentor-launch';
      dom.setAttribute('data-type', 'mentor-launch');
      dom.contentEditable = 'false';
      dom.style.cssText =
        'border:1px solid #cfe2db;border-radius:14px;padding:1.15rem 1.25rem;margin:1.4rem 0;' +
        'background:#fbfdfc;';

      const esc = (s) =>
        String(s == null ? '' : s).replace(
          /[&<>"']/g,
          (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
        );

      const token = () =>
        localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
      const api = (path, opts) =>
        fetch('/api/v1' + path, {
          ...(opts || {}),
          headers: Object.assign(
            { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token() },
            (opts && opts.headers) || {}
          ),
        }).then(async (r) => {
          const j = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(j.message || j.error || 'Something went wrong');
          return j;
        });

      let person = null; // { id?, email, name }
      let course = null; // { id, title }
      let courses = [];

      dom.innerHTML =
        '<div style="font-size:0.7rem;font-weight:800;letter-spacing:0.07em;text-transform:uppercase;color:#2B6B5F;">Try it out</div>' +
        '<h3 style="margin:0.25rem 0 0.35rem;font-size:1.15rem;font-weight:800;color:#17332e;">' +
        esc(node.attrs.heading || 'Start with one person') +
        '</h3>' +
        '<p style="margin:0 0 1rem;font-size:0.9rem;color:#54655F;line-height:1.55;">' +
        'Six sessions is preparation. This is the part that matters. Pick one person and one thing ' +
        'to do together, and it starts when you press the button. They can say no, and that is fine.' +
        '</p>' +
        '<div class="ml-step"><div class="ml-label">1. Who?</div>' +
        '<input class="ml-person" type="text" placeholder="Search your church, or type an email to invite somebody" autocomplete="off">' +
        '<div class="ml-results"></div><div class="ml-chosen"></div></div>' +
        '<div class="ml-step" style="margin-top:1rem;"><div class="ml-label">2. What will you do together?</div>' +
        '<div class="ml-courses">Loading…</div>' +
        '<div style="margin-top:0.6rem;"><label style="font-size:0.82rem;color:#6b7280;">Or choose another: ' +
        '<select class="ml-other" style="margin-left:6px;padding:5px 8px;border:1px solid #d8e5e0;border-radius:8px;font:inherit;font-size:0.85rem;max-width:100%;"></select>' +
        '</label></div></div>' +
        '<div style="margin-top:1.15rem;display:flex;gap:12px;align-items:center;flex-wrap:wrap;">' +
        '<button class="ml-go btn btn-primary" disabled>Start</button>' +
        '<span class="ml-status" style="font-size:0.85rem;color:#6b7280;"></span></div>' +
        // Say what the button does before it is pressed (Bill Goodman, 27 Sep
        // 2026: "I don't know what happens ... it's a little nerve wracking").
        '<p class="ml-preview" style="display:none;margin:0.75rem 0 0;font-size:0.85rem;color:#54655F;line-height:1.55;"></p>';

      const style = document.createElement('style');
      style.textContent =
        '.mentor-launch .ml-label{font-weight:700;font-size:0.9rem;color:#1f2937;margin-bottom:0.4rem;}' +
        '.mentor-launch input.ml-person{width:100%;padding:0.6rem 0.75rem;border:1px solid #d8e5e0;border-radius:10px;font:inherit;font-size:0.92rem;}' +
        '.mentor-launch input.ml-person:focus{outline:2px solid #2B6B5F;outline-offset:1px;}' +
        '.mentor-launch .ml-results{margin-top:6px;}' +
        '.mentor-launch .ml-hit{display:block;width:100%;text-align:left;padding:0.5rem 0.7rem;border:1px solid #eef1ef;' +
        'border-radius:8px;background:#fff;cursor:pointer;font:inherit;font-size:0.88rem;margin-bottom:4px;}' +
        '.mentor-launch .ml-hit:hover{border-color:#2B6B5F;background:#f4f8f6;}' +
        '.mentor-launch .ml-chosen{margin-top:6px;font-size:0.88rem;color:#2B6B5F;font-weight:600;}' +
        '.mentor-launch .ml-courses{display:grid;gap:0.6rem;grid-template-columns:1fr;}' +
        '@media(min-width:640px){.mentor-launch .ml-courses{grid-template-columns:repeat(3,1fr);}}' +
        '.mentor-launch .ml-course{border:1px solid #e3e8e6;border-radius:10px;padding:0.7rem 0.8rem;background:#fff;' +
        'cursor:pointer;text-align:left;font:inherit;}' +
        '.mentor-launch .ml-course:hover{border-color:#2B6B5F;background:#f4f8f6;}' +
        '.mentor-launch .ml-course[aria-pressed="true"]{border-color:#2B6B5F;background:#eef6f4;box-shadow:inset 0 0 0 1px #2B6B5F;}' +
        '.mentor-launch .ml-course b{display:block;font-size:0.9rem;color:#17332e;}' +
        '.mentor-launch .ml-course span{font-size:0.78rem;color:#6b7280;}';
      dom.appendChild(style);

      const $ = (sel) => dom.querySelector(sel);
      const goBtn = $('.ml-go');
      const status = $('.ml-status');

      const preview = $('.ml-preview');
      const refresh = () => {
        goBtn.disabled = !(person && course);
        goBtn.textContent =
          person && course ? 'Start with ' + (person.name || person.email).split(' ')[0] : 'Start';
        if (person && course && !goBtn.dataset.done) {
          const f = esc((person.name || person.email).split(' ')[0]);
          preview.innerHTML =
            '<strong>What happens when you press it:</strong> ' +
            f +
            ' gets one email from MKDSCPLS saying you would like to go through ' +
            esc(course.title) +
            ' together, one on one, with a link to start. If ' +
            f +
            ' replies, it comes to you. Nothing else is sent, and there is no message from you in it, so a quick text or call to say why you asked goes a long way.';
          preview.style.display = '';
        } else if (!goBtn.dataset.done) {
          preview.style.display = 'none';
        }
      };

      // ---- people ---------------------------------------------------------
      let timer = null;
      $('.ml-person').addEventListener('input', (e) => {
        const q = e.target.value.trim();
        person = null;
        $('.ml-chosen').textContent = '';
        refresh();
        clearTimeout(timer);
        if (q.length < 2) {
          $('.ml-results').innerHTML = '';
          return;
        }
        timer = setTimeout(async () => {
          const looksLikeEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(q);
          try {
            const j = await api('/users/search?q=' + encodeURIComponent(q));
            // /users/search answers with a BARE ARRAY, unlike most of the API.
            // Reading it as an envelope silently returned nobody, which looked
            // exactly like "this person is not in your church".
            const list = Array.isArray(j)
              ? j
              : (j.data && (j.data.users || j.data)) || j.users || [];
            let html = list
              .slice(0, 5)
              .map(
                (u) =>
                  '<button type="button" class="ml-hit" data-id="' +
                  esc(u.id) +
                  '" data-email="' +
                  esc(u.email) +
                  '" ' +
                  'data-name="' +
                  esc(((u.firstName || '') + ' ' + (u.lastName || '')).trim()) +
                  '">' +
                  esc(((u.firstName || '') + ' ' + (u.lastName || '')).trim() || u.email) +
                  ' <span style="color:#9ca3af;">' +
                  esc(u.email) +
                  '</span></button>'
              )
              .join('');
            if (
              looksLikeEmail &&
              !list.some((u) => (u.email || '').toLowerCase() === q.toLowerCase())
            ) {
              html +=
                '<button type="button" class="ml-hit" data-email="' +
                esc(q) +
                '" data-name="">' +
                'Invite <b>' +
                esc(q) +
                '</b> <span style="color:#9ca3af;">they do not have an account yet</span></button>';
            }
            $('.ml-results').innerHTML =
              html ||
              '<div style="font-size:0.84rem;color:#9ca3af;padding:4px 2px;">Nobody by that name. Type their email to invite them.</div>';
          } catch (err) {
            $('.ml-results').innerHTML = looksLikeEmail
              ? '<button type="button" class="ml-hit" data-email="' +
                esc(q) +
                '" data-name="">Invite <b>' +
                esc(q) +
                '</b></button>'
              : '<div style="font-size:0.84rem;color:#9ca3af;">Search is unavailable. Type their email instead.</div>';
          }
        }, 250);
      });

      $('.ml-results').addEventListener('click', (e) => {
        const b = e.target.closest('.ml-hit');
        if (!b) return;
        person = {
          id: b.getAttribute('data-id') || null,
          email: b.getAttribute('data-email'),
          name: b.getAttribute('data-name') || '',
        };
        $('.ml-results').innerHTML = '';
        $('.ml-person').value = person.name || person.email;
        $('.ml-chosen').textContent =
          (person.id ? 'Walking with ' : 'Inviting ') + (person.name || person.email);
        refresh();
      });

      // ---- courses --------------------------------------------------------
      const pickCourse = (c, btn) => {
        course = c;
        dom.querySelectorAll('.ml-course').forEach((x) => x.setAttribute('aria-pressed', 'false'));
        if (btn) btn.setAttribute('aria-pressed', 'true');
        refresh();
      };

      (async () => {
        try {
          const j = await api('/courses?limit=200');
          courses = Array.isArray(j)
            ? j
            : (j.data && (j.data.courses || j.data)) || j.courses || [];
          const wanted = String(node.attrs.suggested || '')
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean);
          const picks = wanted.map((slug) => courses.find((c) => c.slug === slug)).filter(Boolean);
          $('.ml-courses').innerHTML = picks.length
            ? picks
                .map(
                  (c) =>
                    '<button type="button" class="ml-course" data-id="' +
                    esc(c.id) +
                    '" aria-pressed="false">' +
                    '<b>' +
                    esc(c.title) +
                    '</b><span>' +
                    // sessions, not weeks: a pair can take one a week, one a month or two in a sitting, and the count is what is fixed
                    (c.duration
                      ? esc(c.duration) + (c.duration === 1 ? ' session' : ' sessions')
                      : 'A short one') +
                    '</span></button>'
                )
                .join('')
            : '<div style="font-size:0.85rem;color:#9ca3af;">Pick one from the list below.</div>';
          dom.querySelectorAll('.ml-course').forEach((btn) => {
            btn.addEventListener('click', () => {
              const c = courses.find((x) => x.id === btn.getAttribute('data-id'));
              if (c) pickCourse({ id: c.id, title: c.title }, btn);
            });
          });
          const sel = $('.ml-other');
          sel.innerHTML =
            '<option value="">Choose a course…</option>' +
            courses
              .map((c) => '<option value="' + esc(c.id) + '">' + esc(c.title) + '</option>')
              .join('');
          sel.addEventListener('change', () => {
            const c = courses.find((x) => x.id === sel.value);
            if (c) pickCourse({ id: c.id, title: c.title }, null);
          });
        } catch (err) {
          $('.ml-courses').innerHTML =
            '<div style="font-size:0.85rem;color:#b91c1c;">Could not load the course list.</div>';
        }
      })();

      // ---- go -------------------------------------------------------------
      goBtn.addEventListener('click', async () => {
        if (!person || !course) return;
        goBtn.disabled = true;
        const first = (person.name || person.email).split(' ')[0];
        status.textContent = 'Setting it up…';
        try {
          let me = JSON.parse(localStorage.getItem('user') || '{}');
          // localStorage can be stale or partial, and /groups requires name,
          // organizationId AND leaderId. Ask who I am rather than hoping.
          if (!me.id || !me.organizationId) {
            const who = await api('/auth/me');
            me = (who.data && who.data.user) || who.user || me;
          }
          const grp = await api('/groups', {
            method: 'POST',
            body: JSON.stringify({
              name: (me.firstName ? me.firstName + ' and ' : '') + (person.name || first),
              groupType: 'mentorship',
              courseId: course.id,
              leaderId: me.id,
              organizationId: me.organizationId,
              description: 'Mentoring through ' + course.title,
            }),
          });
          const group = (grp.data && (grp.data.group || grp.data)) || grp.group || grp;
          const inv = await api('/groups/' + group.id + '/invite', {
            method: 'POST',
            body: JSON.stringify({ email: person.email }),
          });
          const d = (inv && inv.data) || {};
          goBtn.dataset.done = '1';
          preview.style.display = 'none';
          status.style.color = '#2B6B5F';
          status.innerHTML =
            '&#10003; ' +
            esc(first) +
            ' is in ' +
            esc(course.title) +
            ' with you. ' +
            (d.emailSent === false
              ? '<span style="color:#b45309;">The email did not go out' +
                (d.emailReason ? ' (' + esc(d.emailReason) + ')' : '') +
                ', so let ' +
                esc(first) +
                ' know yourself.</span> '
              : 'The email is on its way. ') +
            '<a href="/dashboard.html#groups" style="color:#2B6B5F;font-weight:600;">See it in Groups</a>';
          goBtn.textContent = 'Started';
        } catch (err) {
          goBtn.disabled = false;
          status.style.color = '#b91c1c';
          status.textContent = (err && err.message) || 'Could not start that. Try again.';
        }
      });

      refresh();
      return { dom };
    };
  },
});

export default MentorLaunch;
